<div class="wrapper">
  @if (!warranty) {
    <div class="no-data text-type-8 mt-[80px]">No warranty configured. Go to Settings tab to create one.</div>
  } @else {
    <div class="action-section flex flex-row-reverse my-[20px] w-full gap-[20px]">
      @if (!role?.startsWith('contractor') && canCreate) {
        <span
          [matTooltip]="!isWarrantyActive() ? 'Warranty is not active — claims cannot be created.' : ''"
          matTooltipClass="white-tooltip">
          <app-primary-button
            (handleClick)="createClaim()"
            [isDisabled]="!isWarrantyActive()"
            class="!w-[150px]">
            File Claim
          </app-primary-button>
        </span>
      }

      <div class="claim-filters flex flex-row gap-[20px] flex-1 mt-[7px]">
        <app-tabbed-filter [groups]="filterGroups" />
        <div>
          @if (selectedSortColumn) {
            <mat-form-field class="sort-dropdown">
              <mat-select
                (selectionChange)="onSortColumnChange($event)"
                [(value)]="selectedSortColumn"
                disableOptionCentering
                panelClass="claim-sort-panel">
                <mat-select-trigger>
                  Sort by: {{ selectedSortColumn }}
                </mat-select-trigger>
                @for (column of sortByColumns; track column) {
                  <mat-option [value]="column">{{ column }}</mat-option>
                }
              </mat-select>
              <mat-icon (click)="removeSort($event)" class="sort-close" matSuffix>close</mat-icon>
            </mat-form-field>
          }
          @if (!selectedSortColumn) {
            <div (click)="openSortDropdown($event)" class="sort-dropdown nosort">
              <div class="label text-type-8">Sort:</div>
              <mat-icon>sync_alt</mat-icon>
              <mat-form-field class="sr-sort-hidden">
                <mat-select
                  #sortSelect
                  (selectionChange)="onSortColumnChange($event)"
                  [(value)]="selectedSortColumn"
                  disableOptionCentering
                  panelClass="claim-sort-panel">
                  @for (column of sortByColumns; track column) {
                    <mat-option [value]="column">{{ column }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </div>
          }
        </div>
        <div class="search-section flex flex-1 flex-row gap-[10px] min-w-0">
          <form [formGroup]="form" class="flex-1 min-w-0">
            <div class="search w-full">
              <mat-icon svgIcon="search"></mat-icon>
              <input
                class="search-input"
                formControlName="searchText"
                matInput
                placeholder="Search"
              />
              @if (form?.controls?.['searchText']?.value) {
                <mat-icon (click)="clearSearch()" svgIcon="close"></mat-icon>
              }
            </div>
          </form>
        </div>
      </div>
    </div>

    <div class="claims-list-table flex flex-col gap-[40px]">
      @if (claims.length) {
        <div class="bit-items-table">
          <table [dataSource]="claims" class="mat-elevation-z8 table" mat-table>
            <ng-container matColumnDef="claim_number">
              <th *matHeaderCellDef mat-header-cell>
                <span class="th-text">Claim #</span>
              </th>
              <td *matCellDef="let row" mat-cell>
                <div class="text-type-8">{{ row?.claim_number }}</div>
              </td>
            </ng-container>

            <ng-container matColumnDef="title">
              <th *matHeaderCellDef mat-header-cell>
                <span class="th-text">Title</span>
              </th>
              <td *matCellDef="let row" mat-cell>
                <div class="text-type-8">{{ row?.title }}</div>
                <div class="description text-type-14">{{ row?.description }}</div>
              </td>
            </ng-container>

            <ng-container matColumnDef="severity">
              <th *matHeaderCellDef mat-header-cell>
                <span class="th-text">Severity</span>
              </th>
              <td *matCellDef="let row" mat-cell>
                <div class="status-chip-container">
                  <div
                    [ngClass]="
                      row?.severity && severityMap[row.severity]
                        ? [severityMap[row.severity].bgColor, severityMap[row.severity].textColor]
                        : []
                    "
                    class="status-chip px-[14px] rounded-full">
                    {{ severityMap[row?.severity]?.label }}
                  </div>
                </div>
              </td>
            </ng-container>

            <ng-container matColumnDef="status">
              <th *matHeaderCellDef mat-header-cell>
                <span class="th-text">Status</span>
              </th>
              <td *matCellDef="let row" mat-cell>
                <div class="status-chip-container">
                  <div
                    [ngClass]="
                      row?.status && statusMap[row.status]
                        ? [statusMap[row.status].bgColor, statusMap[row.status].textColor]
                        : []
                    "
                    class="status-chip px-[14px] rounded-full">
                    {{ statusMap[row?.status]?.label }}
                  </div>
                </div>
              </td>
            </ng-container>

            <ng-container matColumnDef="created_at">
              <th *matHeaderCellDef mat-header-cell>
                <span class="th-text">Created</span>
              </th>
              <td *matCellDef="let row" mat-cell>
                <div class="text-type-11">{{ row?.created_at | date:'MMM d, y' }}</div>
              </td>
            </ng-container>

            <ng-container matColumnDef="created_by">
              <th *matHeaderCellDef mat-header-cell>
                <span class="th-text">Created By</span>
              </th>
              <td *matCellDef="let row" mat-cell>
                <div class="text-type-11">{{ row?.createdBy?.first_name }} {{ row?.createdBy?.last_name }}</div>
              </td>
            </ng-container>

            <ng-container matColumnDef="actions">
              <th *matHeaderCellDef mat-header-cell class="actions-col"></th>
              <td *matCellDef="let row" mat-cell class="actions-col">
                @if (row.status !== 'draft') {
                  <button [matMenuTriggerFor]="claimMenu"
                          (click)="$event.stopPropagation()"
                          class="more-menu-button"
                          aria-label="More options">
                    <mat-icon>more_horiz</mat-icon>
                  </button>
                  <mat-menu #claimMenu="matMenu">
                    <button mat-menu-item (click)="downloadClaimPdf(row)">
                      <mat-icon>picture_as_pdf</mat-icon>
                      <span>PDF Report</span>
                    </button>
                    <button mat-menu-item (click)="downloadClaimCsv(row)">
                      <mat-icon>table_chart</mat-icon>
                      <span>CSV Export</span>
                    </button>
                    <button mat-menu-item (click)="downloadClaimKmz(row)">
                      <mat-icon>map</mat-icon>
                      <span>KMZ Map File</span>
                    </button>
                  </mat-menu>
                }
              </td>
            </ng-container>

            <tr *matHeaderRowDef="displayedColumns" mat-header-row></tr>
            <tr
              (click)="rowClicked(row)"
              *matRowDef="let row; columns: displayedColumns"
              [class.highlighted-row]="row === selectedRow"
              mat-row
            ></tr>
          </table>
        </div>
      }

      @if (!claims.length) {
        <div class="no-data text-type-8 mt-[80px]">No warranty claims found.</div>
      }

      <app-paginator
        (pageChanged)="onPageChanged($event)"
        [itemsPerPage]="pagination.pageSize"
        [maxVisiblePages]="5"
        [totalItems]="totalRecords"
        class="paginator"
      />
    </div>

  }
</div>
